import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  initializeSession,
  getCurrentSession,
  getStoredSession,
  shouldShowValidation,
  markValidationShown,
  exchangeHandoffFromUrl,
} from '../utils/sessionManager';
import authService from '../services/auth.service';
import sessionService from '../services/session.service';

const SessionContext = createContext(null);

export const SessionProvider = ({ children }) => {
  const navigate = useNavigate();

  // Session state
  const [session, setSession] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // App-level loading/flow state
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Initializing...');
  const [showValidation, setShowValidation] = useState(false);

  // Helpers
  const navigateClean = useCallback((path) => {
    window.history.replaceState({}, '', path);
    navigate(path, { replace: true });
  }, [navigate]);

  // Initialize session on mount
  useEffect(() => {
    const initializeApp = async () => {
      const currentUrl = window.location.href;

      // OPUS-901/902: secure handoff — if a ?h= token is present, exchange it
      // for a real session (refresh token returned in the body, not the URL),
      // then proceed. Falls through to the legacy ?session= path otherwise.
      if (currentUrl.includes('h=')) {
        const handoffSession = await exchangeHandoffFromUrl();
        if (handoffSession && handoffSession.orgId) {
          setSession(handoffSession);
          setIsLoading(false);
          setIsInitialized(true);
          navigateClean('/competitions');
          return;
        }
      }

      const hasSessionParam = currentUrl.includes('session=');
      const sessionData = initializeSession(currentUrl);

      if (sessionData && sessionData.orgId) {
        setSession(sessionData);

        const verifySession = localStorage.getItem('aether_session_data');
        if (!verifySession) {
          try {
            localStorage.setItem('aether_session_data', JSON.stringify(sessionData));
            const verifyAgain = localStorage.getItem('aether_session_data');
            if (!verifyAgain) {
              setIsLoading(false);
              setIsInitialized(true);
              return;
            }
          } catch (error) {
            setIsLoading(false);
            setIsInitialized(true);
            return;
          }
        }

        if (shouldShowValidation(sessionData)) {
          setShowValidation(true);
          setIsLoading(false);
        } else {
          setIsLoading(false);
          if (hasSessionParam) {
            setTimeout(() => {
              try {
                const urlObj = new URL(currentUrl);
                const currentPath = urlObj.pathname;
                if (currentPath === '/' || currentPath === '') {
                  const { competitionId, seasonId, fixtureId } = sessionData;
                  if (competitionId && seasonId && fixtureId) {
                    navigateClean(`/teamsetup/${competitionId}/${seasonId}/${fixtureId}`);
                  } else {
                    navigateClean('/competitions');
                  }
                } else {
                  urlObj.searchParams.delete('session');
                  const cleanUrl = urlObj.pathname + (urlObj.search || '');
                  window.history.replaceState({}, '', cleanUrl);
                }
              } catch {
                navigateClean('/competitions');
              }
            }, 100);
          }
        }
      } else {
        if (hasSessionParam) {
          setTimeout(() => {
            const retrySession = initializeSession(window.location.href);
            if (retrySession && retrySession.orgId) {
              setSession(retrySession);
              navigateClean('/competitions');
            }
            setIsLoading(false);
          }, 500);
        } else {
          const stored = getCurrentSession();
          if (stored) setSession(stored);
          setIsLoading(false);
        }
      }

      setIsInitialized(true);
    };

    initializeApp();
  }, [navigateClean]);

  // Token refresh interval
  useEffect(() => {
    if ((session || isInitialized) && authService.isAuthenticated()) {
      authService.startTokenRefreshInterval();
    }
    return () => {
      authService.stopTokenRefreshInterval();
    };
  }, [session, isInitialized]);

  // Session expiry check — same as aedrive's TXLiveRoute
  const handleSessionExpiry = useCallback(() => {
    localStorage.clear();
    setSession(null);
    setShowValidation(false);
    setIsLoading(false);
    navigateClean('/');
  }, [navigateClean]);

  useEffect(() => {
    if (!session || isLoading || showValidation) return;

    const checkSession = () => {
      const currentSession = getStoredSession();
      if (!currentSession || !currentSession.orgId) {
        handleSessionExpiry();
        return;
      }
      if (authService.isTokenExpired()) {
        authService.refreshToken().then((refreshed) => {
          if (!refreshed) {
            handleSessionExpiry();
          }
        });
      }
    };

    checkSession();
    const intervalId = setInterval(checkSession, 30000);
    return () => clearInterval(intervalId);
  }, [session, isLoading, showValidation, handleSessionExpiry]);

  // Actions
  const completeValidation = async () => {
    const currentSession = getCurrentSession();
    if (!currentSession?.orgId) {
      setIsLoading(false);
      return;
    }

    markValidationShown(currentSession);
    setShowValidation(false);
    await createNewSession(currentSession);
  };

  const createNewSession = async (currentSession = session) => {
    setIsLoading(true);
    setLoadingMessage('Initializing new session...');

    try {
      await sessionService.createOrUpdateSession();
    } catch (error) {
      console.error('Error creating new session:', error);
    }

    setIsLoading(false);

    // Short URL visitors already have a fully-resolved session (competition,
    // season, fixture) baked into the ?session= payload — send them straight
    // to the team setup page instead of the competitions list.
    const { competitionId, seasonId, fixtureId } = currentSession || {};
    if (competitionId && seasonId && fixtureId) {
      navigateClean(`/teamsetup/${competitionId}/${seasonId}/${fixtureId}`);
    } else {
      navigateClean('/competitions');
    }
  };

  const refreshSession = useCallback(() => {
    const current = getStoredSession();
    setSession(current);
  }, []);

  const value = React.useMemo(() => ({
    // Session
    session,
    isInitialized,
    refreshSession,
    orgId: session?.orgId,
    deviceId: session?.deviceId,
    accessKey: session?.accessKey,
    username: session?.username,
    userId: session?.userId,
    sessionId: session?.sessionId,
    // App flow
    isLoading,
    loadingMessage,
    showValidation,
    completeValidation,
    createNewSession,
  }), [
    session, isInitialized, refreshSession,
    isLoading, loadingMessage, showValidation,
  ]);

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export default SessionContext;
